
/* ============================== src/components/cards/ProjectCard.jsx ============================== */
// path: frontend/src/components/cards/ProjectCard.jsx
/**
 * Карточка проекта - единый компонент для канбана и списка
 * (variant='kanban' | 'list'). Приоритет читается из meta через
 * usePriorityMeta; RBAC — из capabilities + project.permissions
 * (через buildProjectMenu).
 *
 * fact для ProjectProgress берётся из project.actualHours (D-13),
 * локальный пересчёт логов убран.
 *
 * Карточка кликабельна целиком (Clickable as="div"): role="button",
 * tabIndex, Enter/Space — без вложенных <button> (внутри FloatingMenu).
 */
import { memo, useMemo } from "react";
import FloatingMenu from "../FloatingMenu";
import EmployeeCardLink from "../EmployeeCardLink";
import { UnreadBadge } from "../UnreadBadge";
import { PriorityStrip } from "../PriorityStrip";
import ProjectProgress from "../ProjectProgress";
import { TagsList } from "../TagsList";
import { buildProjectMenu } from "../menus/projectMenu";
import { PROJECT_STATUSES } from "../../utils/constants";
import { useAuth } from "../../hooks/useAuth";
import { usePriorityMeta } from "../../hooks/useMetaHelpers";
import { getProjectColor } from "../../utils/projectHelpers";
import { humanizeDeadline, fmtDMY, TODAY } from "../../utils/date";
import { plural } from "../../utils/pluralize";
import { ColorDot, ColoredSpan } from "../ColoredSpan";
import { useChatUnreadCount } from "../../hooks/useChatStats";
import { Clickable } from "../Clickable";

const MAX_VISIBLE_ASSIGNEES = 5;

export const ProjectCard = memo(function ProjectCard({
  project,
  assigneeIds,
  employeesById,
  user,
  variant = "kanban",
  openProject,
  openTag,
  onMove,
  onCopy,
  onMakeTemplate,
  onOpenAccess,
}) {
  const { capabilities } = useAuth();
  const isList = variant === "list";
  const projectColor = getProjectColor(project);

  const unread = useChatUnreadCount(user.id, project.id, null);
  const priorityMeta = usePriorityMeta(project.ptype, project.priority);

  const menuItems = useMemo(
    () =>
      buildProjectMenu({
        project,
        capabilities,
        openProject,
        onMove,
        onCopy,
        onMakeTemplate,
        onOpenAccess,
      }),
    [
      project,
      capabilities,
      openProject,
      onMove,
      onCopy,
      onMakeTemplate,
      onOpenAccess,
    ],
  );

  const handleOpen = () => openProject(project.id);

  const hasTags = Array.isArray(project.tags) && project.tags.length > 0;

  const manager = project.managerId
    ? employeesById.get(project.managerId)
    : null;

  const visibleAssignees = assigneeIds.slice(0, MAX_VISIBLE_ASSIGNEES);
  const hiddenCount = Math.max(0, assigneeIds.length - MAX_VISIBLE_ASSIGNEES);
  const hasAssignees = assigneeIds.length > 0;

  const hasDeadline = !!project.end;
  const deadlineLabel = hasDeadline ? humanizeDeadline(project.end) : null;
  const deadlineIsLate = hasDeadline && project.end < TODAY;

  const rootClassName = isList ? "kcard kcard--list cursor-pointer" : undefined;

  return (
    <FloatingMenu
      items={menuItems}
      children={({ anchorProps }) => (
        <Clickable
          as="div"
          {...anchorProps}
          onClick={handleOpen}
          className={rootClassName}
        >
          <PriorityStrip color={projectColor} />

          <div className="kcard-zone">
            <div className="kcard-title">{project.name}</div>
            {hasTags && (
              <TagsList tags={project.tags} onTagClick={openTag} interactive />
            )}
          </div>

          <div className="kcard-zone">
            <div className="kcard-proj">
              <ColorDot color={projectColor} className="pdot" />
              {project.code}
              {isList && project.status && (
                <span className={`st-chip ${project.status}`}>
                  {PROJECT_STATUSES[project.status]}
                </span>
              )}
              <ColoredSpan
                color={priorityMeta.color}
                className="sm ml-auto rep-priority"
              >
                {priorityMeta.label}
              </ColoredSpan>
            </div>
            {manager && (
              <div className="kcard-manager">
                <span className="kcard-manager-avatar">
                  <EmployeeCardLink employee={manager} size="xs" />
                </span>
                <span className="kcard-manager-name">
                  {manager.first} {manager.last}
                </span>
              </div>
            )}
          </div>

          <div className="kcard-zone">
            <div className="kcard-foot">
              {hasAssignees && (
                <div className="pj-avatars">
                  {visibleAssignees.map((id) => {
                    const a = employeesById.get(id);
                    return a ? (
                      <EmployeeCardLink key={id} employee={a} size="xs" />
                    ) : null;
                  })}
                  {hiddenCount > 0 && (
                    <span
                      className="pj-avatars-more"
                      title={`ещё ${hiddenCount} ${plural(hiddenCount, "исполнитель", "исполнителя", "исполнителей")}`}
                    >
                      +{hiddenCount}
                    </span>
                  )}
                </div>
              )}
              <ProjectProgress project={project} fact={project.actualHours} />
            </div>
            {hasDeadline && (
              <span
                className={`kdl${deadlineIsLate ? " late" : ""}`}
                title={`Срок: ${fmtDMY(project.end)}`}
              >
                {deadlineLabel}
              </span>
            )}
          </div>

          <UnreadBadge
            count={unread}
            onClick={() => openProject(project.id, "chat")}
            variant="neutral"
          />
        </Clickable>
      )}
    />
  );
});
